import capitalize from 'lodash-es/capitalize'
import { Timestamps } from '@/api/global/types/timestamps'
import { Subtask, SubtaskStatus } from '@/api/pathways/subtasks/types'
import { SubtaskTemplateFeature } from '@/api/pathways/subtaskTemplateFeatures/types'
import { IdMap } from '@/legacy/types/api/store'
import { EntityRole } from '@/legacy/types/entities/entities'
import { Form } from '@/legacy/types/forms/forms'
import {
  SubStatusReason,
  TemplateStatus,
} from '@/legacy/types/pathways/pathways'
import { UUID } from '@/libs/typing/base'
import { MemberFacingTheme } from '../admin/pathwaysAdmin'
import { JobStatus } from '../jobs/jobs'
import { CommonTemplateBase } from './common'

// TYPES --------------------
export type SubtaskSuggestionParams = {
  memberId: UUID
  communicationId: UUID
  staffId: UUID
  pageLength: number
}

export type SubtaskOutreachParams = {
  subtaskId: UUID
}

export type GetSubtasksParams = {
  filter_patient_ids?: UUID[] | null
  filter_subtask_ids?: UUID[] | null
  page_length?: number | null
  page_number?: number | null
  filter_free_text?: string | null
  filter_subtask_status?: SubtaskStatus[] | null
  filter_subtask_keys?: string[] | null
  filter_exclude_communication_ids?: string[] | null
  query_parts?: string[] | null
  sort_by?: string[] | null
}

export interface SubtaskThymelineFilters {
  freeTextSearch: string
}

export type SubtaskStatusOption = {
  icon: string
  text: string
  value: SubtaskStatus
  fillType: string | null
  statusOptions: SubtaskStatus[]
}

export type SubtaskPayload = {
  dueDatetime: string
  notes: string | null
  responsibleRole: null | EntityRole
  responsibleStaffId: string | null
  showTime: boolean
  status: SubtaskStatus
  subStatusReason?: SubStatusReason | null
  statusNotes?: string | null
  priority: SubtaskPriority
  customSubject?: string | null
  pointerToCommId?: string | null
  overrideFormCompletionStatus?: boolean | null
  etag?: string | null
}

export type BulkEditSubtaskData = {
  subtaskId: string
  priority?: SubtaskPriority | undefined
  status?: SubtaskStatus | undefined
  responsibleStaffId?: string | undefined
  responsibleRole?: EntityRole | null
  hideDatetime?: Date | undefined
  reattemptReason?: string | undefined
  subStatusReason?: SubStatusReason | null
  isBulkEdited?: boolean | null
  ffPreservePlannedCallAssignee?: boolean | undefined
}

export type BulkEditSubtaskPayload = {
  data: BulkEditSubtaskData[]
  bulkEditNote?: string
}

export type BulkEditSubtaskResponse = {
  runId: string
  status: JobStatus
}

export type SubtaskTemplate = {
  channel: null | string
  baseTemplateId: string
  description: string
  estimatedMinutesToComplete: number | null
  staffCompletableForms?: Form[]
  memberCompletableForms?: Form[]
  hyperlinks: null | string[]
  legacyFormTemplateId: string
  isDeleted?: boolean
  memberFacingTheme: MemberFacingTheme
  responsibleRole: null | EntityRole
  priority: SubtaskPriority
  subtaskKey: string
  subtaskTemplateId: string
  templateStatus: TemplateStatus
  title: string
  variantId: string
  variantKey: string
  variantDisplay: string
  queueStyle: QueueStyle
  outreachMessage?: string
  total?: number
  isMemberFacing?: boolean
  isManuallyAddable: boolean
  reattemptCadence?: Array<number>
  base: SubtaskTemplateBase
  subtaskFeatures?: SubtaskTemplateFeature[]
} & Timestamps

export type SubtaskTemplateBase = {
  subtaskKey: string
  variants?: SubtaskTemplate[]
} & CommonTemplateBase

export type SubtaskOutreachMessage = {
  memberFormLink?: string
  outreachText?: string
}

export type SubtaskOutreachResponse = {
  generated: SubtaskOutreachMessage
}

// INTERFACES --------------------
export interface SubtaskState {
  isLoading: boolean
  error: Error | null
  data: IdMap<Subtask> | null
  isLoaded: boolean
}

export interface SubtaskTemplateDataState {
  isLoading: boolean
  error: Error | null
  data: IdMap<SubtaskTemplate>
  isLoaded: boolean
  queryMetadata: {
    total: number
  } | null
}

export interface SubtaskTemplateBaseDataState {
  isLoading: boolean
  error: Error | null
  data: IdMap<SubtaskTemplateBase>
  isLoaded: boolean
  queryMetadata: {
    total: number
  } | null
}

// ENUMS --------------------
export enum SubtaskOutreachType {
  EMAIL = 'email',
  TEXT = 'text',
}

export enum SubtaskPriority {
  URGENT = 'URGENT',
  HIGH = 'HIGH',
  ROUTINE = 'ROUTINE',
  OPTIONAL = 'OPTIONAL',
}

export enum SubtaskVisibility {
  ONLY_HIDDEN = 'ONLY_HIDDEN',
  NO_HIDDEN = 'NO_HIDDEN',
  INCLUDE_HIDDEN = 'INCLUDE_HIDDEN',
}
export enum ScheduledCallSubtasks {
  INCLUDE = 'INCLUDE',
  EXCLUDE = 'EXCLUDE',
}

export enum SubtaskRowDisplayType {
  SUBTASK_DETAIL = 'SUBTASK_DETAIL',
  TASK_ACCORDION = 'TASK_ACCORDION',
  THYMELINE = 'THYMELINE',
  /**
   * STATUS_UPDATE_MODAL: Used for modals that update subtask status.
   * This encapsulates Reattempt and SubtaskSubStatus modals.
   * Use this for any future modals with similar behavior or styling
   * related to updating subtask status.
   */
  STATUS_UPDATE_MODAL = 'STATUS_UPDATE_MODAL',
  MY_QUEUE = 'MY_QUEUE',
  SIDEBAR_QUEUE_STACK = 'SIDEBAR_QUEUE_STACK',
  ENROLLMENT = 'ENROLLMENT',
}

export enum QueueStyle {
  STANDARD = 'STANDARD',
  ENROLLMENT = 'ENROLLMENT',
}

// CONSTANTS --------------------
export const MAX_NUM_SUGGESTIONS = 3
export const MAX_NUM_SUBTASKS = 10
export const MAX_CHAR_SUBTASK_SUBJECT_LINE = 50

export const subtaskVisibilityEnumVal = {
  ONLY_HIDDEN: 'Only hidden',
  NO_HIDDEN: 'No hidden',
  INCLUDE_HIDDEN: 'Include hidden',
}
export const scheduledCallEnumVal = {
  INCLUDE: 'Only',
  EXCLUDE: 'None',
}
export const onlyOpenSubtaskStatuses = [
  SubtaskStatus.OPEN_UNASSIGNED,
  SubtaskStatus.OPEN_ASSIGNED,
]

export const openAssignedSubtaskStatuses = [
  SubtaskStatus.IN_PROGRESS,
  SubtaskStatus.OPEN_ASSIGNED,
  SubtaskStatus.ON_HOLD,
]

export const openSubtaskStatuses = [
  ...openAssignedSubtaskStatuses,
  SubtaskStatus.OPEN_UNASSIGNED,
]
export const openSubtaskStatusesWithOnHold = [
  ...openAssignedSubtaskStatuses,
  SubtaskStatus.OPEN_UNASSIGNED,
  SubtaskStatus.ON_HOLD,
]
export const closedSubtaskStatuses = [
  SubtaskStatus.CANCELLED,
  SubtaskStatus.COMPLETED,
  SubtaskStatus.CLOSED_IN_ERROR,
]

export const gvpSubtaskKeys = [
  'complete_assmnt_gvp',
  'complete_assmnt_gvp_gen',
  'complete_assmnt_gvp_ob',
]

// Used to identify subtasks that should render the "Find Help Template" button
// in their notes section
export const findHelpTemplateButtonSubtaskKeys = [
  'cp_consult_finance',
  'cp_consult_transportation',
  'cp_consult_food',
  'cp_consult_housing',
  'cp_consult_ihsupport',
]

// Used in subtaskTypeKeyValMap to reduce duplication
export const caseConferenceSubtaskKeys = [
  'complete_cc_new',
  'complete_cc_new_gen',
  'complete_cc_essential',
  'complete_cc_fu',
  'complete_cc_gvp',
  'complete_cc_acp_gen',
  'complete_cc_palcare',
  'complete_cc_onc',
  'complete_cc_raps',
  'complete_cc_essential',
  'complete_cc_sic',
  'complete_cc_toc',
]

export const subtaskTypeKelValMap = {
  'multi_vals-subtask_type-complete_assmnt_eddc': [
    'complete_assmnt_eddc',
    'complete_assmnt_eddc_ed',
    'complete_assmnt_edcc_complex',
  ],
  'multi_vals-subtask_type-complete_assmnt_ipa': [
    'complete_assmnt_ipa',
    'complete_assmnt_ipa_ia',
  ],
  'multi_vals-subtask_type-complete_assmnt_dc': [
    'complete_assmnt_dc',
    'complete_assmnt_dc_id',
    'complete_assmnt_dc_toc',
    'complete_assmnt_dc_complex',
    'complete_assmnt_dc_id_complex',
  ],
  'multi_vals-subtask_type-complete_assmnt_pacdc': [
    'complete_assmnt_pacdc',
    'complete_assmnt_pacdc_pac',
    'complete_assmnt_pacdc_complex',
  ],
  'multi_vals-subtask_type-symptom_id_mobile': [
    'migrated_Symptom_Identification_f21267e4-0245-4bcc-bc9e-b01c0d0539dd',
    'symptom_id_mobile_call',
    'symptom_id_mobile_eom_call',
    'symptom_id_mobile_eom_call_spanish',
    'symptom_id_mobile_call_spanish',
  ],

  'multi_vals-subtask_type-respond_sms': [
    'respond_sms_cp',
    'respond_sms_es',
    'respond_sms_ob_rn',
  ],
  'multi_vals-subtask_type-case_conference': caseConferenceSubtaskKeys,
  'multi_vals-subtask_type-prepare_eom_care_plan_emr': [
    'eom_care_plan_prep',
    'eom_care_plan_prep_gen',
    'eom_care_plan_prep_offboarding',
  ],
  'multi_vals-subtask_type-complete_clinical_onboarding': [
    'complete_assmnt_onboard_rn',
    'complete_assmnt_onboard_rn_mo',
    'complete_assmnt_onboard_eg',
  ],
  'multi_vals-subtask_type-evaluate_and_assess_symptoms': [
    'confirm_epro',
    'reassess_epro',
    'assess_epro',
    'assess_epro_bh',
  ],
  'multi_vals-subtask_type-schedule_post_discharge_toc_event': [
    'schedule_toc_pdv',
    'schedule_pdv_toc_event',
    'schedule_pdv_toc',
  ],
  'multi_vals-subtask_type-create_ap_ip': ['create_ap_ip_toc', 'create_ap_ip'],
  'multi_vals-subtask_type-send_fax_gen': [
    'send_fax_gen_es',
    'send_fax_gen_rn',
    'send_fax_gen_cp',
  ],
  'multi_vals-subtask_type-send_acp_note': [
    'send_cc_acp_all',
    'send_cc_acp_onc',
  ],
  'multi_vals-subtask_type-send_toc_note': [
    'send_cc_toc_all',
    'send_toc_ccnote_onc_unenrolled',
  ],
}

export const subtaskTypeFilterIndicator = 'subtask_type'

export const incompleteStatuses = [
  ...openSubtaskStatuses,
  SubtaskStatus.ON_HOLD,
  SubtaskStatus.BLOCKED,
]

export const priorityOptions = Object.entries(SubtaskPriority).map(
  ([key, value]) => {
    return {
      value: key,
      label: capitalize(value),
    }
  }
)

export const queueStyleOptions = Object.keys(QueueStyle).map((style) => {
  return {
    value: style,
    label: style,
  }
})

export const memberFacingThemeOptions = [
  { label: 'None', value: null },
  ...Object.keys(MemberFacingTheme).map((theme) => ({
    label: MemberFacingTheme[theme as keyof typeof MemberFacingTheme],
    value: theme,
  })),
]
export interface SubtaskRowExposed {
  updateDirtySubtask: (data: Partial<SubtaskPayload>) => Promise<void>
}

export interface FormsortExposed {
  saveFormResponse: () => Promise<void>
}
